// src/features/system/admin/components/RbacManagementV2.tsx
//
// RBAC Management — enterprise-grade toggle-based permission management.
//
// Tabs:
//   1. Roles → Permissions — boolean toggle matrix per role
//   2. User → Roles        — boolean toggle per user-role binding

import React, { useCallback, useMemo, useState } from 'react';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';
import {
  useIAMRoles,
  useIAMUsers,
  useSetRolePermissions,
  useUpdateIAMRoles,
  useInvalidateIAMSessions,
} from '@/features/system/iam/hooks/useIAMUsers';
import type { IAMRole, IAMUser } from '@/features/system/iam/api/iamApi';

/* ─── Sub-tab types ───────────────────────────────────────── */

type SubTab = 'role-perms' | 'user-roles';

const TABS: { key: SubTab; label: string }[] = [
  { key: 'role-perms', label: 'Roles → Permissions' },
  { key: 'user-roles', label: 'User → Roles' },
];

/* ─── Toggle Switch ───────────────────────────────────────── */

function Toggle({
  enabled,
  onChange,
  disabled = false,
  loading = false,
}: {
  enabled: boolean;
  onChange: (next: boolean) => void;
  disabled?: boolean;
  loading?: boolean;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={enabled}
      disabled={disabled || loading}
      onClick={() => onChange(!enabled)}
      className={`
        relative inline-flex h-5 w-9 flex-shrink-0 cursor-pointer
        rounded-full border border-transparent transition-colors duration-200
        focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500/50
        ${disabled ? 'opacity-30 cursor-not-allowed' : ''}
        ${loading ? 'opacity-60' : ''}
        ${enabled ? 'bg-emerald-600' : 'bg-neutral-700'}
      `}
    >
      <span
        className={`
          pointer-events-none inline-block h-4 w-4 rounded-full
          bg-white shadow-sm ring-0 transition-transform duration-200 ease-in-out
          ${enabled ? 'translate-x-4' : 'translate-x-0'}
        `}
      />
    </button>
  );
}

/* ═══════════════════════════════════════════════════════════════
   Panel 1 — Role → Permissions (master-detail split)
   Left sidebar: role list · Right panel: permission toggles
   ═══════════════════════════════════════════════════════════════ */

function deriveDomain(perm: string): string {
  const i = perm.indexOf('.');
  return i === -1 ? perm : perm.substring(0, i);
}

function RolePermissionsPanel({ roles }: { roles: IAMRole[] }) {
  const setPermsMutation = useSetRolePermissions();
  const [selectedRoleKey, setSelectedRoleKey] = useState<string | null>(
    roles.length > 0 ? roles[0].role_key : null,
  );
  const [filter, setFilter] = useState('');

  const selectedRole = useMemo(
    () => roles.find(r => r.role_key === selectedRoleKey) ?? null,
    [roles, selectedRoleKey],
  );

  // Group roles by scope: "global" (no dot) vs domain prefix (text before first dot)
  const roleGroups = useMemo(() => {
    const m = new Map<string, IAMRole[]>();
    for (const role of roles) {
      const dot = role.role_key.indexOf('.');
      const scope = dot === -1 ? 'global' : role.role_key.substring(0, dot);
      if (!m.has(scope)) m.set(scope, []);
      m.get(scope)!.push(role);
    }
    const entries = Array.from(m.entries());
    entries.sort(([a], [b]) => {
      if (a === 'global') return -1;
      if (b === 'global') return 1;
      return a.localeCompare(b);
    });
    return entries;
  }, [roles]);

  // Build the universal permission catalog from all roles.
  const allPermissions = useMemo(() => {
    const s = new Set<string>();
    roles.forEach(r => r.permissions.forEach(p => { if (p !== '*') s.add(p); }));
    return Array.from(s).sort();
  }, [roles]);

  // Group permissions by domain.
  const domainGroups = useMemo(() => {
    const m = new Map<string, string[]>();
    for (const p of allPermissions) {
      const d = deriveDomain(p);
      if (!m.has(d)) m.set(d, []);
      m.get(d)!.push(p);
    }
    return Array.from(m.entries()).sort(([a], [b]) => a.localeCompare(b));
  }, [allPermissions]);

  // Filtered domains for the right panel.
  const filteredGroups = useMemo(() => {
    if (!filter.trim()) return domainGroups;
    const q = filter.toLowerCase();
    return domainGroups
      .map(([domain, perms]) => {
        const filteredPerms = perms.filter(p => p.toLowerCase().includes(q));
        if (filteredPerms.length > 0 || domain.toLowerCase().includes(q)) {
          return [domain, filteredPerms.length > 0 ? filteredPerms : perms] as [string, string[]];
        }
        return null;
      })
      .filter(Boolean) as [string, string[]][];
  }, [domainGroups, filter]);

  const handleToggle = useCallback(
    (role: IAMRole, perm: string, currentlyEnabled: boolean) => {
      const newPerms = currentlyEnabled
        ? role.permissions.filter(p => p !== perm)
        : [...role.permissions, perm];
      setPermsMutation.mutate({ roleKey: role.role_key, permissions: newPerms });
    },
    [setPermsMutation],
  );

  const isWildcard = selectedRole ? selectedRole.permissions.includes('*') : false;

  return (
    <div className="flex border border-neutral-800 rounded-lg overflow-hidden" style={{ height: 'calc(100vh - 260px)', minHeight: 360 }}>
      {/* ── Left sidebar: role list ── */}
      <div className="w-52 shrink-0 flex flex-col border-r border-neutral-800 min-h-0">
        {/* Sidebar header */}
        <div className="px-3 py-2.5 border-b border-neutral-800 flex items-center justify-between">
          <span className="text-xs font-medium text-neutral-300">Roles</span>
          <span className="text-[10px] text-neutral-600">{roles.length}</span>
        </div>

        {/* Role items — grouped by scope */}
        <div className="flex-1 min-h-0 overflow-y-auto">
          {roleGroups.map(([scope, scopeRoles]) => (
            <div key={scope}>
              {/* Scope section header */}
              <div className="px-3 py-1.5 text-[9px] font-mono uppercase tracking-widest text-amber-500/60 pac-glass border-b border-neutral-800/50 sticky top-0 z-10 flex items-center justify-between">
                <span>{scope}</span>
                <span className="text-neutral-700">{scopeRoles.length}</span>
              </div>
              {scopeRoles.map(role => {
                const active = role.role_key === selectedRoleKey;
                const isWc = role.permissions.includes('*');
                const permCount = isWc ? '∗' : String(role.permissions.length);

                return (
                  <button
                    key={role.role_key}
                    type="button"
                    onClick={() => { setSelectedRoleKey(role.role_key); setFilter(''); }}
                    className={
                      'w-full text-left px-3 py-2.5 border-b border-neutral-800/50 ' +
                      'flex items-center gap-2 transition-colors cursor-default group ' +
                      (active
                        ? 'pac-glass text-neutral-100 border-l-2 border-l-emerald-500'
                        : 'text-neutral-400 hover:bg-black hover:text-neutral-200 border-l-2 border-l-transparent')
                    }
                  >
                    <div className="flex-1 min-w-0">
                      <div className="text-xs truncate">
                        {role.display_name || role.role_key}
                      </div>
                      <div className="text-[10px] font-mono text-neutral-600 truncate">
                        {role.role_key}
                      </div>
                    </div>

                    <span
                      className={
                        'shrink-0 rounded-full px-1.5 py-0.5 text-[10px] leading-none ' +
                        (active ? 'bg-emerald-700 text-white' : 'pac-glass-subtle text-neutral-500')
                      }
                    >
                      {permCount}
                    </span>
                  </button>
                );
              })}
            </div>
          ))}
        </div>
      </div>

      {/* ── Right panel: permission toggles for selected role ── */}
      <div className="flex-1 min-w-0 flex flex-col min-h-0">
        {!selectedRole ? (
          <div className="flex-1 flex items-center justify-center">
            <span className="text-sm text-neutral-600">Select a role</span>
          </div>
        ) : (
          <>
            {/* Role detail header */}
            <div className="px-4 py-3 border-b border-neutral-800 shrink-0 flex items-center justify-between gap-3">
              <div className="flex items-center gap-3 min-w-0">
                <span className="text-sm font-semibold text-neutral-200 truncate">
                  {selectedRole.display_name || selectedRole.role_key}
                </span>
                <span className="text-[10px] font-mono text-neutral-600">{selectedRole.role_key}</span>
                {selectedRole.is_system && (
                  <span className="text-[9px] bg-sky-900/30 text-sky-400 px-1.5 py-0.5 rounded border border-sky-800/30 shrink-0">
                    system
                  </span>
                )}
              </div>
              <div className="flex items-center gap-3 shrink-0">
                <input
                  type="text"
                  placeholder="Filter permissions…"
                  value={filter}
                  onChange={e => setFilter(e.target.value)}
                  className="w-44 bg-transparent border border-neutral-800 rounded px-2 py-1
                             text-xs text-neutral-300 placeholder-neutral-600
                             focus:outline-none focus:border-neutral-600 transition-colors"
                />
                <span className="text-[10px] text-neutral-500">
                  {isWildcard ? '∗ all' : `${selectedRole.permissions.length} perms`}
                </span>
              </div>
            </div>

            {/* Description */}
            {selectedRole.description && (
              <div className="px-4 py-2 border-b border-neutral-800/50 shrink-0">
                <p className="text-xs text-neutral-500">{selectedRole.description}</p>
              </div>
            )}

            {/* Wildcard banner */}
            {isWildcard && (
              <div className="mx-4 mt-3 flex items-center gap-2 text-xs bg-rose-900/20 border border-rose-800/40 rounded px-3 py-2 text-rose-300 shrink-0">
                <span className="font-mono font-bold">*</span>
                <span>Superadmin — all permissions granted implicitly</span>
              </div>
            )}

            {/* Permission toggles — horizontal domain columns */}
            <div className="flex-1 min-h-0 overflow-auto px-4 py-3">
              <div className="flex gap-3 items-start min-w-max">
                {filteredGroups.map(([domain, perms]) => {
                  const enabledCount = perms.filter(
                    p => isWildcard || selectedRole.permissions.includes(p),
                  ).length;

                  return (
                    <div
                      key={domain}
                      className="rounded-lg border border-neutral-800/60 pac-glass overflow-hidden flex-shrink-0"
                      style={{ width: 220 }}
                    >
                      {/* Domain column header */}
                      <div className="px-3 py-2 border-b border-neutral-800/40 pac-glass">
                        <div className="flex items-center justify-between">
                          <span className="text-[10px] font-mono uppercase tracking-wider text-amber-500/70">
                            {domain}
                          </span>
                          <span
                            className={`text-[9px] font-mono px-1.5 py-0.5 rounded ${
                              enabledCount === perms.length
                                ? 'bg-emerald-900/30 text-emerald-400'
                                : enabledCount > 0
                                  ? 'bg-amber-900/20 text-amber-400/70'
                                  : 'pac-glass-subtle text-neutral-600'
                            }`}
                          >
                            {enabledCount}/{perms.length}
                          </span>
                        </div>
                      </div>

                      {/* Permission rows */}
                      <div className="px-3 py-1.5 space-y-0.5">
                        {perms.map(perm => {
                          const enabled = isWildcard || selectedRole.permissions.includes(perm);
                          const shortLabel = perm.startsWith(domain + '.')
                            ? perm.substring(domain.length + 1)
                            : perm;

                          return (
                            <label
                              key={perm}
                              className="flex items-center justify-between gap-2 py-1 group cursor-pointer"
                              title={perm}
                            >
                              <span className="text-xs font-mono text-neutral-400 group-hover:text-neutral-200 transition-colors truncate">
                                {shortLabel}
                              </span>
                              <Toggle
                                enabled={enabled}
                                onChange={() => handleToggle(selectedRole, perm, enabled)}
                                disabled={isWildcard}
                                loading={setPermsMutation.isPending}
                              />
                            </label>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>

              {filteredGroups.length === 0 && (
                <div className="flex items-center justify-center py-8">
                  <p className="text-xs text-neutral-600 italic">No permissions match the filter.</p>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════
   Panel 2 — User → Roles (toggle per user)
   ═══════════════════════════════════════════════════════════════ */

function UserRolesPanel({ users, roles }: { users: IAMUser[]; roles: IAMRole[] }) {
  const updateRolesMut = useUpdateIAMRoles();
  const invalidateSessionsMut = useInvalidateIAMSessions();
  const [selectedUserId, setSelectedUserId] = useState<number | null>(
    users.length > 0 ? users[0].id_user : null,
  );
  const [filter, setFilter] = useState('');
  const [invalidateScope, setInvalidateScope] = useState<'user' | 'tenant'>('user');
  const [tenantInput, setTenantInput] = useState('');
  const [invalidateMsg, setInvalidateMsg] = useState<string | null>(null);

  const selectedUser = useMemo(
    () => users.find(u => u.id_user === selectedUserId) ?? null,
    [users, selectedUserId],
  );

  React.useEffect(() => {
    if (selectedUser?.tenant_id != null) {
      setTenantInput(String(selectedUser.tenant_id));
    }
  }, [selectedUser?.tenant_id]);

  // Group roles by scope — same pattern as RolePermissionsPanel
  const roleGroups = useMemo(() => {
    const m = new Map<string, IAMRole[]>();
    for (const role of roles) {
      const dot = role.role_key.indexOf('.');
      const scope = dot === -1 ? 'global' : role.role_key.substring(0, dot);
      if (!m.has(scope)) m.set(scope, []);
      m.get(scope)!.push(role);
    }
    const entries = Array.from(m.entries());
    entries.sort(([a], [b]) => {
      if (a === 'global') return -1;
      if (b === 'global') return 1;
      return a.localeCompare(b);
    });
    return entries;
  }, [roles]);

  // Filter role groups by search term
  const filteredGroups = useMemo(() => {
    if (!filter.trim()) return roleGroups;
    const q = filter.toLowerCase();
    return roleGroups
      .map(([scope, scopeRoles]) => {
        const filtered = scopeRoles.filter(
          r =>
            r.role_key.toLowerCase().includes(q) ||
            r.display_name.toLowerCase().includes(q),
        );
        if (filtered.length > 0 || scope.toLowerCase().includes(q)) {
          return [scope, filtered.length > 0 ? filtered : scopeRoles] as [string, IAMRole[]];
        }
        return null;
      })
      .filter(Boolean) as [string, IAMRole[]][];
  }, [roleGroups, filter]);

  const handleToggle = useCallback(
    (user: IAMUser, roleKey: string, currentlyEnabled: boolean) => {
      const newRoles = currentlyEnabled
        ? user.roles.filter(r => r !== roleKey)
        : [...user.roles, roleKey];
      updateRolesMut.mutate({ idUser: user.id_user, roles: newRoles });
    },
    [updateRolesMut],
  );

  const STATUS_DOT: Record<string, string> = {
    active:   'bg-emerald-400',
    inactive: 'bg-neutral-600',
    locked:   'bg-rose-500',
  };

  const targetTenant = Number(tenantInput);
  const canRunInvalidate = invalidateScope === 'user'
    ? Boolean(selectedUser)
    : Number.isFinite(targetTenant) && targetTenant > 0;

  const runInvalidate = useCallback(() => {
    if (!canRunInvalidate) return;
    const targetId = invalidateScope === 'user'
      ? Number(selectedUser?.id_user)
      : targetTenant;
    const targetLabel = invalidateScope === 'user'
      ? `user ${selectedUser?.username ?? selectedUser?.id_user}`
      : `tenant ${targetTenant}`;
    const ok = window.confirm(
      `Invalidate active sessions for ${targetLabel}?\n\nAffected users will automatically revalidate auth claims on next request.`,
    );
    if (!ok) return;

    setInvalidateMsg(null);
    invalidateSessionsMut.mutate(
      { scope: invalidateScope, targetId },
      {
        onSuccess: (res) => {
          setInvalidateMsg(`Done: ${res.updated_sessions} active session(s) invalidated.`);
        },
        onError: (e: any) => {
          setInvalidateMsg(`Failed: ${e?.message ?? 'Unknown error'}`);
        },
      },
    );
  }, [canRunInvalidate, invalidateScope, invalidateSessionsMut, selectedUser, targetTenant]);

  return (
    <div className="flex border border-neutral-800 rounded-lg overflow-hidden" style={{ height: 'calc(100vh - 260px)', minHeight: 360 }}>
      {/* ── Left sidebar: user list ── */}
      <div className="w-52 shrink-0 flex flex-col border-r border-neutral-800 min-h-0">
        {/* Sidebar header */}
        <div className="px-3 py-2.5 border-b border-neutral-800 flex items-center justify-between">
          <span className="text-xs font-medium text-neutral-300">Users</span>
          <span className="text-[10px] text-neutral-600">{users.length}</span>
        </div>

        {/* User items */}
        <div className="flex-1 min-h-0 overflow-y-auto">
          {users.map(user => {
            const active = user.id_user === selectedUserId;
            const roleCount = user.roles.length;

            return (
              <button
                key={user.id_user}
                type="button"
                onClick={() => { setSelectedUserId(user.id_user); setFilter(''); }}
                className={
                  'w-full text-left px-3 py-2.5 border-b border-neutral-800/50 ' +
                  'flex items-center gap-2 transition-colors cursor-default group ' +
                  (active
                    ? 'pac-glass text-neutral-100 border-l-2 border-l-emerald-500'
                    : 'text-neutral-400 hover:bg-black hover:text-neutral-200 border-l-2 border-l-transparent')
                }
              >
                <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${STATUS_DOT[user.status] ?? 'bg-neutral-600'}`} />
                <div className="flex-1 min-w-0">
                  <div className="text-xs truncate">
                    {user.username}
                  </div>
                  {user.full_name && (
                    <div className="text-[10px] text-neutral-600 truncate">
                      {user.full_name}
                    </div>
                  )}
                </div>

                <span
                  className={
                    'shrink-0 rounded-full px-1.5 py-0.5 text-[10px] leading-none ' +
                    (active ? 'bg-emerald-700 text-white' : 'pac-glass-subtle text-neutral-500')
                  }
                >
                  {roleCount}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {/* ── Right panel: role toggles for selected user ── */}
      <div className="flex-1 min-w-0 flex flex-col min-h-0">
        {!selectedUser ? (
          <div className="flex-1 flex items-center justify-center">
            <span className="text-sm text-neutral-600">Select a user</span>
          </div>
        ) : (
          <>
            {/* User detail header */}
            <div className="px-4 py-3 border-b border-neutral-800 shrink-0 flex items-center justify-between gap-3">
              <div className="flex items-center gap-3 min-w-0">
                <span className={`w-2 h-2 rounded-full flex-shrink-0 ${STATUS_DOT[selectedUser.status] ?? 'bg-neutral-600'}`} />
                <span className="text-sm font-semibold text-neutral-200 truncate">
                  {selectedUser.username}
                </span>
                {selectedUser.full_name && (
                  <span className="text-[10px] text-neutral-600">{selectedUser.full_name}</span>
                )}
                <span className={`text-[9px] px-1.5 py-0.5 rounded border shrink-0 ${
                  selectedUser.status === 'active'
                    ? 'bg-emerald-900/30 text-emerald-400 border-emerald-800/30'
                    : selectedUser.status === 'locked'
                      ? 'bg-rose-900/30 text-rose-400 border-rose-800/30'
                      : 'pac-glass-subtle text-neutral-500 border-neutral-700/30'
                }`}>
                  {selectedUser.status}
                </span>
              </div>
              <div className="flex items-center gap-3 shrink-0">
                <input
                  type="text"
                  placeholder="Filter roles…"
                  value={filter}
                  onChange={e => setFilter(e.target.value)}
                  className="w-44 bg-transparent border border-neutral-800 rounded px-2 py-1
                             text-xs text-neutral-300 placeholder-neutral-600
                             focus:outline-none focus:border-neutral-600 transition-colors"
                />
                <span className="text-[10px] text-neutral-500">
                  {selectedUser.roles.length} role{selectedUser.roles.length !== 1 ? 's' : ''}
                </span>
              </div>
            </div>

            <div className="px-4 py-2 border-b border-neutral-800/50 flex items-center gap-2 flex-wrap">
              <span className="text-[10px] text-neutral-500 uppercase tracking-wider">Revalidate access now</span>
              <select
                value={invalidateScope}
                onChange={e => setInvalidateScope(e.target.value as 'user' | 'tenant')}
                className="bg-transparent border border-neutral-800 rounded px-2 py-1 text-xs text-neutral-300"
              >
                <option value="user">Selected user</option>
                <option value="tenant">Tenant</option>
              </select>
              {invalidateScope === 'tenant' && (
                <input
                  type="number"
                  min={1}
                  value={tenantInput}
                  onChange={e => setTenantInput(e.target.value)}
                  placeholder="tenant id"
                  className="w-28 bg-transparent border border-neutral-800 rounded px-2 py-1 text-xs text-neutral-300 placeholder-neutral-600"
                />
              )}
              <button
                type="button"
                onClick={runInvalidate}
                disabled={!canRunInvalidate || invalidateSessionsMut.isPending}
                className="px-2.5 py-1 rounded border border-amber-700/60 text-amber-300 text-xs hover:bg-amber-900/20 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {invalidateSessionsMut.isPending ? 'Applying…' : 'Invalidate + Revalidate'}
              </button>
              {invalidateMsg && (
                <span className="text-[11px] text-neutral-400">{invalidateMsg}</span>
              )}
            </div>

            {/* Role toggle cards — grouped by domain */}
            <div className="flex-1 min-h-0 overflow-y-auto px-4 py-3 space-y-3">
              {filteredGroups.map(([scope, scopeRoles]) => {
                const enabledCount = scopeRoles.filter(
                  r => selectedUser.roles.includes(r.role_key),
                ).length;

                return (
                  <div
                    key={scope}
                    className="rounded-lg border border-neutral-800/60 pac-glass overflow-hidden"
                  >
                    {/* Scope card header */}
                    <div className="flex items-center justify-between px-3 py-2 border-b border-neutral-800/40 pac-glass">
                      <div className="flex items-center gap-2">
                        <span className="text-[10px] font-mono uppercase tracking-wider text-amber-500/70">
                          {scope}
                        </span>
                        <span className="text-[9px] text-neutral-600">
                          {scopeRoles.length} role{scopeRoles.length !== 1 ? 's' : ''}
                        </span>
                      </div>
                      <span
                        className={`text-[9px] font-mono px-1.5 py-0.5 rounded ${
                          enabledCount === scopeRoles.length
                            ? 'bg-emerald-900/30 text-emerald-400'
                            : enabledCount > 0
                              ? 'bg-amber-900/20 text-amber-400/70'
                              : 'pac-glass-subtle text-neutral-600'
                        }`}
                      >
                        {enabledCount}/{scopeRoles.length}
                      </span>
                    </div>

                    {/* Role toggles grid */}
                    <div className="px-3 py-2.5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-1">
                      {scopeRoles.map(role => {
                        const enabled = selectedUser.roles.includes(role.role_key);
                        // Strip domain prefix for cleaner display
                        const shortLabel = role.role_key.includes('.')
                          ? role.display_name
                          : role.display_name;

                        return (
                          <label
                            key={role.role_key}
                            className="flex items-center justify-between gap-3 py-1 group cursor-pointer"
                            title={`${role.role_key} — ${role.description}`}
                          >
                            <div className="min-w-0 flex-1">
                              <span className="text-xs text-neutral-400 group-hover:text-neutral-200 transition-colors truncate block">
                                {shortLabel}
                              </span>
                              <span className="text-[10px] font-mono text-neutral-600 truncate block">
                                {role.role_key}
                              </span>
                            </div>
                            <Toggle
                              enabled={enabled}
                              onChange={() => handleToggle(selectedUser, role.role_key, enabled)}
                              loading={updateRolesMut.isPending}
                            />
                          </label>
                        );
                      })}
                    </div>
                  </div>
                );
              })}

              {filteredGroups.length === 0 && (
                <div className="flex items-center justify-center py-8">
                  <p className="text-xs text-neutral-600 italic">No roles match the filter.</p>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════
   Main component
   ═══════════════════════════════════════════════════════════════ */

export default function RbacManagementV2() {
  const [activeTab, setActiveTab] = useState<SubTab>('role-perms');
  const activeTabLabel = TABS.find(t => t.key === activeTab)?.label ?? '';
  useDocumentTitle('Admin', 'IAM', 'RBAC', activeTabLabel);

  const { data: rolesData, isLoading: rolesLoading, isError: rolesError, error: rolesErr } = useIAMRoles();
  const { data: usersData, isLoading: usersLoading } = useIAMUsers({ limit: 200 });

  const roles: IAMRole[] = rolesData?.items ?? [];
  const users: IAMUser[] = usersData?.items ?? [];

  const loading = rolesLoading || (activeTab === 'user-roles' && usersLoading);

  return (
    <section className="space-y-4">
      {/* Header */}
      <div className="space-y-1">
        <div className="flex items-center gap-3">
          <h2 className="text-xl font-semibold">RBAC Management</h2>
          <span className="text-[10px] bg-emerald-900/30 text-emerald-400 px-2 py-0.5 rounded border border-emerald-800/30">
            IAM
          </span>
        </div>
        <p className="text-xs text-neutral-500">
          Role-based access control.
          {roles.length > 0 && (
            <span className="ml-1">
              {roles.length} role{roles.length !== 1 ? 's' : ''} ·{' '}
              {users.length} user{users.length !== 1 ? 's' : ''}
            </span>
          )}
        </p>
      </div>

      {/* Sub-tabs */}
      <div className="flex gap-1 border-b border-neutral-800 pb-0 overflow-x-auto">
        {TABS.map(t => (
          <button
            key={t.key}
            onClick={() => setActiveTab(t.key)}
            className={`px-3 py-2 text-xs whitespace-nowrap rounded-t transition-colors ${
              activeTab === t.key
                ? 'pac-glass text-neutral-100 border-b-2 border-emerald-500 font-semibold'
                : 'text-neutral-500 hover:text-neutral-300 hover:border-neutral-600'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Error */}
      {rolesError && (
        <div className="bg-rose-900/20 border border-rose-800 rounded p-3 text-sm text-rose-300">
          Failed to load roles: {rolesErr?.message ?? 'Unknown error'}
        </div>
      )}

      {/* Loading */}
      {loading && (
        <p className="text-neutral-400 text-sm py-4">Loading…</p>
      )}

      {/* Panel content */}
      {!loading && !rolesError && (
        <div className="pt-2">
          {activeTab === 'role-perms' && <RolePermissionsPanel roles={roles} />}
          {activeTab === 'user-roles' && <UserRolesPanel users={users} roles={roles} />}
        </div>
      )}
    </section>
  );
}
